import { useState } from "react";
import { rsvpApi } from "../services/api";
import { AuthResponse, MultipleEventsResponse } from "../types";

interface AuthFormProps {
  onAuthenticated: (
    data: AuthResponse | MultipleEventsResponse,
    phone: string
  ) => void;
}

export function AuthForm({ onAuthenticated }: AuthFormProps) {
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestForm, setRequestForm] = useState({ name: "", phone: "", note: "" });
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [requestError, setRequestError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!phone.trim()) {
      setError("Please enter your phone number");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const data = await rsvpApi.authenticate(
        phone.trim(),
        phone.trim() // Use phone number as access code
      );
      onAuthenticated(data, phone.trim());
    } catch (err: any) {
      setError(
        err.response?.data?.error || "Invalid phone number or event not found"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRequestAccess = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!requestForm.name.trim() || !requestForm.phone.trim()) {
      setRequestError("Name and phone number are required");
      return;
    }

    setRequestLoading(true);
    setRequestError("");

    try {
      await rsvpApi.requestMembership({
        name: requestForm.name,
        phone: requestForm.phone,
        note: requestForm.note
        // hostId will be determined by backend (routes to primary admin)
      });
      setRequestSuccess(true);
    } catch (err: any) {
      setRequestError(
        err.response?.data?.error || "Failed to submit request. Please try again."
      );
    } finally {
      setRequestLoading(false);
    }
  };

  return (
    <div className="auth-container">
      {/* Animated background elements */}
      <div className="bg-animation">
        <div className="floating-shape shape-1"></div>
        <div className="floating-shape shape-2"></div>
        <div className="floating-shape shape-3"></div>
        <div className="floating-shape shape-4"></div>
      </div>

      <div className="auth-card">
        {/* Header with logo and branding */}
        <div className="auth-header">
          <div className="logo-container">
            <div className="logo-icon">
              <img
                src="/logo.svg"
                alt="There's Enough to go Around"
                className="brand-logo"
              />
            </div>
          </div>
          {/* Subtitle removed */}
        </div>

        {/* Form section */}
        <form onSubmit={handleSubmit} className="auth-form">
          {error && (
            <div className="error-message">
              <svg className="error-icon" viewBox="0 0 24 24" fill="none">
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <line
                  x1="15"
                  y1="9"
                  x2="9"
                  y2="15"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <line
                  x1="9"
                  y1="9"
                  x2="15"
                  y2="15"
                  stroke="currentColor"
                  strokeWidth="2"
                />
              </svg>
              {error}
            </div>
          )}

          <div className="input-group">
            <div className="input-wrapper">
              <label className="input-label" htmlFor="phone">
                Access Code
              </label>
              <input
                id="phone"
                type="tel"
                className="modern-input"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="5551234567"
                disabled={loading}
              />
            </div>
          </div>

          <button type="submit" className="submit-button" disabled={loading}>
            {loading ? (
              <>
                <div className="button-spinner"></div>
                <span>Verifying...</span>
              </>
            ) : (
              <>
                <svg className="button-icon" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M5 12h14M12 5l7 7-7 7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>View Invitation</span>
              </>
            )}
          </button>

          <div className="divider">
            <span>or</span>
          </div>

          <button
            type="button"
            className="secondary-button"
            onClick={() => setShowRequestForm(true)}
          >
            <svg className="button-icon" viewBox="0 0 24 24" fill="none">
              <path
                d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M12.5 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0zM20 8v6M23 11h-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>Request Access</span>
          </button>
        </form>

        {/* Request Access Modal */}
        {showRequestForm && (
          <div className="modal-overlay" onClick={() => !requestSuccess && setShowRequestForm(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              {requestSuccess ? (
                <div className="success-state">
                  <div className="success-icon">
                    <svg viewBox="0 0 24 24" fill="none">
                      <path
                        d="M22 11.08V12a10 10 0 1 1-5.93-9.14"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M22 4L12 14.01l-3-3"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <h2 className="success-title">Request Submitted!</h2>
                  <p className="success-message">
                    Thank you for your interest! The admin will review your request, and if approved, you'll be eligible to receive invitations to events.
                  </p>
                  <button
                    className="submit-button"
                    onClick={() => {
                      setShowRequestForm(false);
                      setRequestSuccess(false);
                      setRequestForm({ name: "", phone: "", note: "" });
                    }}
                  >
                    Close
                  </button>
                </div>
              ) : (
                <>
                  <div className="modal-header">
                    <h2 className="modal-title">Request Access</h2>
                    <button
                      className="close-button"
                      onClick={() => setShowRequestForm(false)}
                    >
                      <svg viewBox="0 0 24 24" fill="none">
                        <line
                          x1="18"
                          y1="6"
                          x2="6"
                          y2="18"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <line
                          x1="6"
                          y1="6"
                          x2="18"
                          y2="18"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>
                  </div>

                  <p className="modal-description">
                    If a friend referred you, submit your information below. The admin will review and approve your request to join the member list.
                  </p>

                  <form onSubmit={handleRequestAccess} className="request-form">
                    {requestError && (
                      <div className="error-message">
                        <svg className="error-icon" viewBox="0 0 24 24" fill="none">
                          <circle
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="2"
                          />
                          <line
                            x1="15"
                            y1="9"
                            x2="9"
                            y2="15"
                            stroke="currentColor"
                            strokeWidth="2"
                          />
                          <line
                            x1="9"
                            y1="9"
                            x2="15"
                            y2="15"
                            stroke="currentColor"
                            strokeWidth="2"
                          />
                        </svg>
                        {requestError}
                      </div>
                    )}

                    <div className="input-group">
                      <label className="input-label" htmlFor="request-name">
                        Your Name *
                      </label>
                      <input
                        id="request-name"
                        type="text"
                        className="modern-input"
                        value={requestForm.name}
                        onChange={(e) =>
                          setRequestForm({ ...requestForm, name: e.target.value })
                        }
                        placeholder="Enter your full name"
                        disabled={requestLoading}
                      />
                    </div>

                    <div className="input-group">
                      <label className="input-label" htmlFor="request-phone">
                        Phone Number *
                      </label>
                      <input
                        id="request-phone"
                        type="tel"
                        className="modern-input"
                        value={requestForm.phone}
                        onChange={(e) =>
                          setRequestForm({ ...requestForm, phone: e.target.value })
                        }
                        placeholder="5551234567"
                        disabled={requestLoading}
                      />
                    </div>

                    <div className="input-group">
                      <label className="input-label" htmlFor="request-note">
                        Note (Optional)
                      </label>
                      <textarea
                        id="request-note"
                        className="modern-textarea"
                        value={requestForm.note}
                        onChange={(e) =>
                          setRequestForm({ ...requestForm, note: e.target.value })
                        }
                        placeholder="Who referred you or any additional information..."
                        rows={3}
                        disabled={requestLoading}
                      />
                    </div>

                    <div className="modal-actions">
                      <button
                        type="button"
                        className="cancel-button"
                        onClick={() => setShowRequestForm(false)}
                        disabled={requestLoading}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="submit-button"
                        disabled={requestLoading}
                      >
                        {requestLoading ? (
                          <>
                            <div className="button-spinner"></div>
                            <span>Submitting...</span>
                          </>
                        ) : (
                          <span>Submit Request</span>
                        )}
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
